import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { itemCountPerPage } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { downloadFile } from 'services'
import { downloadUtmLogs, getUtmLogs } from 'services/apis/utms'
import { svgSearch } from 'stories/assets'
import { Input, Pagination, PlainTable } from 'stories/components'
import { formatDate } from 'utils'
import { copyClipboard } from 'utils/copyClipboard'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constant'
import type { UtmLog } from './types'

export function ManageUtmLogs() {
  const location = useLocation()
  const navigate = useHistory()
  const keywordParam = new URLSearchParams(location.search).get('keyword')

  const [isLoading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [pageNum, setPageNum] = useState(0)
  const [filterQuery, setFilterQuery] = useState('')
  const [values, setValues] = useState<UtmLog[]>([])
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    orderBy: 'createdAt',
    orderDir: '-1',
  })
  const [isGetUsersOnce, setIsGetUsersOnce] = useState(false)
  const inputs = defaultInputs()

  useEffect(() => {
    if (isLoading) return
    filterData(filters).then(() => {
      setIsGetUsersOnce(true)
    })
  }, [pageNum, keywordParam])

  useEffect(() => {
    if (!isGetUsersOnce) return
    const timeOutId = setTimeout(() => !isLoading && filterData(filters, 0), 700)
    return () => clearTimeout(timeOutId)
  }, [filterQuery])

  const onChangeFilter = (key: string, value: any) => {
    if (!isGetUsersOnce) return
    const newFilters = Object.assign({}, filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key === 'query') setFilterQuery(value)
    else {
      filterData(newFilters, 0)
      setPageNum(0)
    }
  }

  const filterData = async (filters: any, _pageNum: number = -1) => {
    if (isLoading) return
    if (_pageNum === -1) _pageNum = pageNum
    const filterData = {
      ...filters,
      skip: pageNum * itemCountPerPage,
      count: itemCountPerPage,
      ...(keywordParam && { keyword: keywordParam }),
    }

    setLoading(true)
    const { data, total } = await getUtmLogs(filterData)
    setValues(data)
    setTotal(total)
    setLoading(false)
  }

  const onPageNavigate = (num: number) => {
    setPageNum(num)
  }

  const onDownload = async () => {
    const res: Blob = await downloadUtmLogs()

    downloadFile('utmlogs.xlsx', res)
  }

  const goBackToAnalysis = () => {
    navigate.push('/admin_tool/utm?tab=analysis')
  }

  const headers = useMemo(() => {
    const baseHeaders = ['No', 'Email', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'UTM Term', 'UTM Content']
    if (!keywordParam) {
      baseHeaders.push('Keyword')
    }
    baseHeaders.push('URL', 'IP Address', 'Created At')
    return baseHeaders
  }, [keywordParam])

  const tableData = useMemo(() => {
    return values.map((value, index) => {
      const row: Array<string | number | JSX.Element> = [
        index + 1 + pageNum * itemCountPerPage,
        value.email || '-',
        value.utm_source || '-',
        value.utm_medium || '-',
        value.utm_campaign || '-',
        value.utm_term || '-',
        value.utm_content || '-',
      ]

      if (!keywordParam) {
        row.push(value.keyword || '-')
      }

      row.push(
        value.url ? (
          <button
            onClick={() => copyClipboard(value.url)}
            className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-blue-600 transition-colors"
            title="Copy URL"
          >
            Copy
          </button>
        ) : (
          '-'
        ),
        value.ip_address || '-',
        formatDate(value.createdAt),
      )

      return row
    })
  }, [values, pageNum, keywordParam])

  return (
    <div>
      <div className="relative">
        <div className="flex flex-wrap justify-between items-center">
          <h1 className="text-2xl font-variation-settings-600 flex items-center mb-3">
            <span>UTM Logs{keywordParam && ` (${keywordParam})`}</span>
          </h1>
          {keywordParam && (
            <button
              onClick={goBackToAnalysis}
              className="px-4 py-2 mb-3 text-sm bg-primary text-white rounded hover:bg-blue-600 transition-colors"
            >
              Back to Analysis
            </button>
          )}
        </div>

        <div className="flex flex-row justify-between mb-3">
          {!keywordParam && (
            <Input
              type="search"
              title="Search ..."
              hasIcon
              icon={svgSearch}
              value={filters.query}
              className="!w-96"
              onChange={(value) => onChangeFilter('query', value)}
            />
          )}

          {keywordParam && (
            <div className="grid gap-4 md:grid-cols-3 grid-cols-1 w-full">
              <div className="input md:col-span-1">
                <RenderInput input={{ ...inputs.email, value: filters.email }} Key="email" onChange={onChangeFilter} />
              </div>
            </div>
          )}
          <div className="flex flex-row gap-3">
            <span className="p-1 text-sm cursor-pointer text-shade-blue rounded" onClick={onDownload}>
              <ArrowDownTrayIcon className="w-6 h-6 p-1 hover-shadow1" />
            </span>
          </div>
        </div>

        <div className="relative">
          <LayoutLoading show={isLoading} />
          <PlainTable header={headers} data={tableData} tdClass="px-2 py-2" thClass="px-2 py-3" />
        </div>

        <div className="flex justify-end items-center mt-3">
          <div className="flex-1" />
          <Pagination
            totalCount={total}
            itemCountPerPage={itemCountPerPage}
            onNavigate={onPageNavigate}
            pageNum={pageNum}
          />
        </div>
      </div>
    </div>
  )
}
